import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap"
import { useLocation, useNavigate } from "react-router-dom";
import ESpinner from "components/others/Spinner";
import EmptyResult from "components/others/EmptyResult";
import ObjectcardInfo from "./ObjectCardInfo";
import ObjectCardTabs from "./ObjectCardTabs";
import ObjectCardToolBar from "./ObjectCardToolBar";
import { OffClientInfo } from 'modules/client';
import { OffTasks } from 'modules/task';
import { ObjectCardProvider, useObjectCard } from "./context/ObjectCardProvider";
import ObjectStatusBadge from "../ObjectsList/ObjectStatus/ObjectStatusBadge";
import { useGlobal } from "context/GlobalContext";
import SpinnerBlock from "components/others/SpinnerBlock";
import vconst from "utils/helpers/structs/vconst";
import * as apiObject from "api/apiObjects";
import { useTranslation } from "react-i18next";
import useChangeDate from "hooks/useChangeDate";
import { useToast } from "context/ToastContext";
import { useSettings } from "context/SettingsContext";
import { DateTime as DT } from "luxon";
import { getObjectCardPath } from "utils/helpers/objects/objectCardPath";
import ObjectVat from "../ObjectsList/ObjectVat/ObjectVat";

 
const ObjectCard = () => {

    const { g } = useGlobal();
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { getSettings } = useSettings();
    const { changeDate } = useChangeDate();
    const location = useLocation();
    const navigate = useNavigate();
    const { objectId, vObject, fetchObject, showSpinner, setShowSpinner, showAnalytics, isEditLocked, onlyService, isSaleLike } = useObjectCard();
    const [ clientInfoId, setClientInfoId] = useState(null);
    const [ showTasks, setShowTasks ] = useState(false);
    const [ priceSummary, setPriceSummary ] = useState(0);

    const dt = DT.fromMillis(vObject?.object?.objectFinished||0).toLocaleString(DT.DATE_MED);
    

    useEffect(()=>{
        fetchObject(); 
    },[objectId, g.target])

    useEffect(() => {
        const obj = vObject?.object;
        if (!obj?.ID) return;
        const isServiceObject = Number(obj.onlyService) === 1;
        const isSaleObject = Number(obj.onlySale) === 1;
        const onServiceSalesRoute = location.pathname.startsWith('/service-sales');
        const onSalesRoute = location.pathname.startsWith('/sales');
        if (isServiceObject && !onServiceSalesRoute) {
            navigate(getObjectCardPath(obj.ID, false, true), { replace: true });
            return;
        }
        if (isSaleObject && !isServiceObject && !onSalesRoute) {
            navigate(getObjectCardPath(obj.ID, true), { replace: true });
            return;
        }
        if (!isSaleObject && !isServiceObject && (onSalesRoute || onServiceSalesRoute)) {
            navigate(getObjectCardPath(obj.ID, false), { replace: true });
        }
    }, [vObject?.object?.ID, vObject?.object?.onlySale, vObject?.object?.onlyService, location.pathname, navigate]);

    
    const handleChangeStatus = async (statusData) => {
        setShowSpinner(true)
        const prevStatus = vObject?.object?.objectStatus;
        const resStatus = await apiObject.setStatus(vObject.object.ID, statusData);
        let ok = false;
        if (resStatus?.errCode === 'RESERV_WAREHOUSE_NOT_EMPTY') {
            const messKey = statusData?.status === vconst.objectReject
                ? 'messReservWarehouseNotEmptyCancel'
                : 'messReservWarehouseNotEmptyFinished';
            showToast(t(messKey), 'danger');
        } else if (resStatus?.errCode === 'HAS_WARRANTIES') {
            showToast(t('messHasWarranties'), 'danger');
        } else if (resStatus?.errCode === 'COMMENT_REQUIRED') {
            showToast(t('objectClose.errComment'), 'danger');
        } else if (resStatus?.errCode === 'LOCKED') {
            showToast(t('objectClose.errLocked'), 'danger');
        } else if (!resStatus?.stat) {
            showToast(t('common:messError'), 'danger');
        } else {
            ok = true;
            if (statusData?.status === vconst.objectFinished) {
                showToast(t('messObjectFinished'), 'success');
            } else if (statusData?.status === vconst.objectReject) {
                showToast(t('messObjectCancel'), 'success');
            } else if (statusData?.status === vconst.objectInWork) {
                const returnedFromClosed = [vconst.objectFinished, vconst.objectReject].includes(prevStatus);
                const takenIntoWork = prevStatus === vconst.objectPanding;
                showToast(
                    returnedFromClosed
                        ? t('objectClose.messReturned')
                        : takenIntoWork
                            ? t('messObjectInWork')
                            : (isSaleLike ? t('messSaleReturnToWork') : t('messObjectReturnToTeam')),
                    'success',
                );
            }
            const returnedToWork = statusData?.status === vconst.objectInWork
                && [vconst.objectFinished, vconst.objectReject].includes(prevStatus);
            if (returnedToWork || [vconst.objectFinished, vconst.objectReject, vconst.objectPanding].includes(statusData?.status)) {
                // Оновлюємо список складів для сайдбару (резерв зникає/з'являється)
                await getSettings();
            }
        }
        await fetchObject();
        return ok;
    }    

    const handleClickDate = async (newFinishedDate) => {
        setShowSpinner(true) 
        const resDate = await apiObject.setFinished(vObject.object.ID, newFinishedDate);
        await fetchObject();
        if (!resDate) showToast(t('common:messError'), 'danger');        
    }
    

    return (<>
        <OffClientInfo
            clientId={clientInfoId}
            setClientId={setClientInfoId}/>

        <OffTasks
            show={showTasks}
            onHide={() => setShowTasks(false)}
            objectId={vObject?.object?.ID ?? null}
            contextName={vObject?.object?.objectName || vObject?.object?.clientName || null}/>

            <Row> 
                <Col className="col-sticky" sm={12}>
                    <div className="object-card__header">
                        <div className="object-card__identity">
                            <div className="object-card__title-row">
                                <h4
                                    className="object-card__title text-truncate"
                                    title={vObject?.object?.objectName || vObject?.object?.clientName || ''}
                                >
                                    {vObject?.object?.objectName || vObject?.object?.clientName}
                                </h4>
                                <div className="object-card__badges">
                                    {!showSpinner && vObject?.object?.objectStatus != null ? (
                                        <span title={`${t(`status.${vObject?.object?.objectStatus}`)}${vObject?.object?.objectStatus === vconst.objectFinished ? `: ${dt}` : ''}`}
                                            className={
                                                vObject?.object?.objectStatus === vconst.objectFinished && !isEditLocked
                                                    ? 'cursor-pointer'
                                                    : ''
                                            }
                                            onClick={(e)=>{
                                                const status = vObject?.object?.objectStatus;
                                                if (status !== vconst.objectFinished) return;
                                                if (isEditLocked) return;
                                                if (e.ctrlKey)
                                                    changeDate(vObject?.object?.objectFinished, handleClickDate);
                                            }}>
                                            <ObjectStatusBadge status={vObject?.object?.objectStatus} />
                                        </span>
                                    ) : null}
                                    <ObjectVat isVat={vObject?.object?.objectVat}/>
                                </div>
                            </div>
                            {!isSaleLike ? (
                                <span className="object-card__team">{vObject?.object?.teamName}</span>
                            ) : null}
                        </div>
                        <div className="object-card__toolbar">
                            <ObjectCardToolBar  
                                handleChangeStatus={handleChangeStatus}
                                setClientInfoId={setClientInfoId}
                                onShowTasks={() => setShowTasks(true)}/>
                        </div>
                    </div>
                </Col>

                {vObject?.object 
                ? (<>
                    <Col xl={showAnalytics ? 8 : 12} xxl={showAnalytics ? 9 : 12} className="pb-3">   
                        <ObjectCardTabs/> 
                    </Col>

                    {showAnalytics ? (
                        <Col xl={4} xxl={3}>
                            <ObjectcardInfo
                                priceSummary={priceSummary}
                                setPriceSummary={setPriceSummary}/>
                        </Col>
                    ) : null}
                </>)

                : showSpinner
                ? (<Col className="py-3"><ESpinner show={true}/></Col>)
                : (<Col className="py-3"><EmptyResult/></Col>)}

            </Row>
        </>)
}

export default ObjectCard;